import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2, Map as MapIcon, X } from 'lucide-react';
import MapPointPicker from '../MapPointPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One integrated delivery-address module: type to see matching address picks
// (forward geocode) AND/OR drop a pin on the map (reverse geocode fills the
// text). Both paths resolve precise lat/lng for the delivery location.
export default function DeliveryAddressField({
  address, lat, lng, onChange, inputCls = '',
  placeholder = 'Piegādes adrese *', fallbackLat, fallbackLng, testId = 'cart-address',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const debRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => { document.removeEventListener('mousedown', h); if (debRef.current) clearTimeout(debRef.current); };
  }, []);

  // Autocomplete fires ONLY while the user types (never when the value is set
  // from a saved-address chip, a map pin, or when the cart opens).
  const handleType = (e) => {
    const v = e.target.value;
    onChange?.({ address: v, lat: null, lng: null });
    const q = v.trim();
    if (debRef.current) clearTimeout(debRef.current);
    if (q.length < 4) { setSuggestions([]); setOpen(false); return; }
    debRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/geocode`, { params: { q } });
        const list = (data?.results || []).slice(0, 6);
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch { setSuggestions([]); setOpen(false); }
      finally { setSearching(false); }
    }, 450);
  };

  const pick = (s) => {
    onChange?.({ address: s.address || s.display_name || '', lat: s.lat, lng: s.lng });
    setSuggestions([]); setOpen(false);
  };

  const handlePin = async (plat, plng) => {
    setGeoLoading(true);
    try {
      const { data } = await axios.get(`${API}/locations/reverse`, { params: { lat: plat, lng: plng } });
      onChange?.({ address: data?.address || address || '', lat: plat, lng: plng });
    } catch {
      onChange?.({ address: address || '', lat: plat, lng: plng });
    } finally { setGeoLoading(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid="delivery-address-field">
      <div className="relative">
        <Input value={address || ''} onChange={handleType} onFocus={() => suggestions.length && setOpen(true)}
          placeholder={placeholder} className={`${inputCls} pr-9`} data-testid={testId} autoComplete="off" />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)] pointer-events-none">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-xl border border-[var(--border-input)] bg-[var(--bg-surface-secondary)] shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => pick(s)}
              className="w-full text-left px-3 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-card)] border-b border-[var(--border-divider)] last:border-0 flex items-start gap-2"
              data-testid={`address-suggestion-${i}`}>
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent)]" />
              <span className="min-w-0">{s.address || s.display_name}</span>
            </button>
          ))}
        </div>
      )}

      <button type="button" onClick={() => setMapOpen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)] hover:underline"
        data-testid="toggle-map-picker">
        {mapOpen ? <><X className="w-4 h-4" /> Aizvērt karti</> : <><MapIcon className="w-4 h-4" /> Izvēlēties kartē</>}
      </button>

      {mapOpen && (
        <div className="mt-2 space-y-1.5" data-testid="cart-map-wrap">
          <MapPointPicker lat={lat} lng={lng} centerLat={fallbackLat} centerLng={fallbackLng} onChange={handlePin} />
          <p className="text-xs text-[var(--text-faint)] flex items-center gap-1.5">
            {geoLoading ? <><Loader2 className="w-3 h-3 animate-spin" /> Nosaku adresi…</> : 'Pieskaries kartei vai pārvelc pogu, lai izvēlētos precīzu piegādes vietu.'}
          </p>
        </div>
      )}
    </div>
  );
}
